import React, { useState, useEffect } from 'react';
import { nfConfirm } from '../components/confirm';
import DeliveryTariffEditor, { validateTariffRows } from '../components/DeliveryTariffEditor';
import WarehouseMapPicker from '../components/WarehouseMapPicker';
import AdminAddressAutocomplete from '../components/AdminAddressAutocomplete';
import { api } from '../api';
import { BRAND_NAME } from '../brand';
import Modal from '../components/Modal';
import NumInput from '../components/NumInput';
import OrderStatusesSettings from '../components/OrderStatusesSettings';
import TransferPointsSettings from '../components/TransferPointsSettings';
import { EditIcon, TrashIcon } from '../components/ActionIcons';

/** Эквайринг онлайн-оплат — ставка ЮKassa по договору, параметр магазина рядом с сервисным сбором
 *  (владелец 25.10: из настроек статистики сюда). Считает её аналитика — «осталось магазину» у
 *  онлайн-оплат; ручка только у владельца, у админа карточки нет */
function AcquiringCard({ showToast }) {
  const [value, setValue] = useState(null);
  useEffect(() => {
    api.getAnalyticsSettings().then(s => setValue(s.acquiring_percent ?? '')).catch(() => {});
  }, []);
  if (value === null) return null;
  const save = async () => {
    try {
      const s = await api.saveAnalyticsSettings({ acquiring_percent: value });
      setValue(s.acquiring_percent ?? '');
      showToast('Сохранено');
    } catch (e) {
      showToast(e.message || 'Не сохранилось', 'error');
    }
  };
  return (
    <div className="card">
      <div className="card-title">Эквайринг онлайн-оплат</div>
      <p style={{ color: '#666', fontSize: 14, marginBottom: 16 }}>
        Ставка ЮKassa по договору. В аналитике вычитается из «осталось магазину» у заказов, оплаченных онлайн. По умолчанию 4 %, 0 — не учитывать
      </p>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <input className="form-input" style={{ width: 100 }} inputMode="decimal" value={value}
          onChange={(e) => setValue(e.target.value)} aria-label="Ставка эквайринга, %" />
        <span style={{ color: '#666' }}>%</span>
        <button className="btn btn-primary btn-sm" onClick={save}>
          Сохранить
        </button>
      </div>
    </div>
  );
}

function SettingsPage({ showToast }) {
  const [settings, setSettings] = useState({
    service_fee_percent: 11,
    bonus_percent: 3,
    min_order_amount: 1500,
    postcard_price: 199,
    max_delivery_distance_km: 40,
    bouquet_service_fee_percent: 20,
    catalog_horizon_days: 14,
    order_lead_time_minutes: 120,
  });
  const [courierCommission, setCourierCommission] = useState(20);
  const [packerWorkflowEnabled, setPackerWorkflowEnabled] = useState(false);
  const [photoControlEnabled, setPhotoControlEnabled] = useState(false);
  const [photoRetentionDays, setPhotoRetentionDays] = useState(60);
  const [pickupPoints, setPickupPoints] = useState([]);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [blockForm, setBlockForm] = useState({
    date_from: '',
    date_to: '',
    time_slots: []
  });
  // Тарифы доставки: произвольные интервалы (строки могут быть недозаполнены
  // во время ввода — валидация перед сохранением в validateTariffRows)
  const [tariffRows, setTariffRows] = useState([]);
  const [warehouse, setWarehouse] = useState({ lat: null, lon: null });
  const [warehouseAddress, setWarehouseAddress] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('general');

  // Скидки подписок
  const [discountTiers, setDiscountTiers] = useState([]);
  const [newTier, setNewTier] = useState({ min_deliveries: '', discount_percent: '' });

  // Certificate config
  const [certConfig, setCertConfig] = useState({
    min_amount: 3000,
    max_amount: 50000,
    validity_days: 90,
    show_banner: false,
  });

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingPoint, setEditingPoint] = useState(null);
  const [pointForm, setPointForm] = useState({
    name: '',
    address: '',
    price: 0,
    is_active: true
  });

  // Suppliers state
  const [suppliers, setSuppliers] = useState([]);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [showAllSuppliers, setShowAllSuppliers] = useState(false);
  const [supplierForm, setSupplierForm] = useState({
    name: '', contact_person: '', phone: '', comment: '', is_active: true,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [settingsData, pointsData, blockedSlotsData, tariffsData, packerWorkflow, courierCommData, tiersData] = await Promise.all([
        api.getSettings(),
        api.getPickupPoints(),
        api.getBlockedSlots(),
        api.getDeliveryTariffIntervals(),
        api.getPackerWorkflowEnabled(),
        api.getCourierCommission(),
        api.getSubscriptionDiscountTiers(),
      ]);
      setSettings(settingsData);
      setPickupPoints(pointsData);
      setBlockedSlots(blockedSlotsData);
      setTariffRows((tariffsData.intervals || []).map(i => ({ up_to_km: String(i.up_to_km), price: String(i.price) })));
      setWarehouse({ lat: tariffsData.warehouse_lat, lon: tariffsData.warehouse_lon });
      // Адрес склада не хранится — только координаты; после перезахода строка поиска
      // была пустой. Показываем адрес сохранённой точки обратным геокодом, как при
      // перетаскивании маркера (замечание юзера 2026-09-16). Не блокируем загрузку.
      if (Number.isFinite(tariffsData.warehouse_lat) && Number.isFinite(tariffsData.warehouse_lon)) {
        api.geolocateAddress(tariffsData.warehouse_lat, tariffsData.warehouse_lon)
          .then((r) => { if (r && r.address) setWarehouseAddress(r.address); })
          .catch(() => { /* адрес не определился — поле останется пустым */ });
      }
      setPackerWorkflowEnabled(packerWorkflow.enabled);
      api.getPhotoControlEnabled().then(r => { setPhotoControlEnabled(!!r.enabled); if (r.retention_days) setPhotoRetentionDays(r.retention_days); }).catch(() => {});
      setCourierCommission(courierCommData.percent);
      setDiscountTiers(tiersData.sort((a, b) => a.min_deliveries - b.min_deliveries));
      try {
        const cc = await api.getCertificateConfig();
        setCertConfig(cc);
      } catch (e) {
        // ignore — пока миграция не накатилась эндпоинт может вернуть дефолты
      }
    } catch (error) {
      showToast('Ошибка загрузки', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const updateCertConfig = async () => {
    try {
      await api.updateCertificateConfig(certConfig);
      showToast('Настройки сертификатов обновлены');
    } catch (error) {
      showToast('Ошибка сохранения', 'error');
    }
  };

  const updateServiceFee = async () => {
    try {
      await api.updateServiceFee(settings.service_fee_percent);
      showToast('Сервисный сбор обновлён');
    } catch (error) {
      showToast('Ошибка сохранения', 'error');
    }
  };

  const updateBonusPercent = async () => {
    try {
      await api.updateBonusPercent(settings.bonus_percent);
      showToast('Процент пополнения счёта обновлён');
    } catch (error) {
      showToast('Ошибка сохранения', 'error');
    }
  };

  const updateMinOrderAmount = async () => {
    try {
      await api.updateMinOrderAmount(settings.min_order_amount);
      showToast('Минимальная сумма заказа обновлена');
    } catch (error) {
      showToast('Ошибка сохранения', 'error');
    }
  };

  const updatePostcardPrice = async () => {
    try {
      await api.updatePostcardPrice(settings.postcard_price);
      showToast('Цена открытки обновлена');
    } catch (error) {
      showToast('Ошибка сохранения', 'error');
    }
  };

  const updateCourierCommission = async () => {
    try {
      console.log('[Settings] Updating courier commission:', courierCommission);
      const result = await api.updateCourierCommission(courierCommission);
      console.log('[Settings] Update result:', result);
      showToast('Комиссия курьера обновлена');
    } catch (error) {
      console.error('[Settings] Error updating courier commission:', error);
      showToast(error.message || 'Ошибка сохранения', 'error');
    }
  };

  const updateMaxDisplayQuantity = async () => {
    try {
      await api.updateMaxDisplayQuantity(settings.max_display_quantity || null);
      showToast(settings.max_display_quantity ? 'Лимит количества сохранён' : 'Лимит количества снят');
    } catch (error) {
      showToast('Ошибка сохранения', 'error');
    }
  };

  const togglePhotoControl = async () => {
    try {
      await api.setPhotoControlEnabled(!photoControlEnabled);
      setPhotoControlEnabled(!photoControlEnabled);
      showToast(photoControlEnabled ? 'Фото-контроль выключен' : 'Фото-контроль включён');
    } catch (error) {
      showToast(error.message || 'Ошибка сохранения', 'error');
    }
  };

  const savePhotoRetention = async () => {
    const days = parseInt(photoRetentionDays, 10);
    if (!days || days < 1 || days > 365) { showToast('Срок хранения: от 1 до 365 дней', 'error'); return; }
    try {
      await api.setPhotoControlEnabled(undefined, days);
      showToast('Срок хранения фото сохранён');
    } catch (error) {
      showToast(error.message || 'Ошибка сохранения', 'error');
    }
  };

  const togglePackerWorkflow = async () => {
    try {
      await api.setPackerWorkflowEnabled(!packerWorkflowEnabled);
      setPackerWorkflowEnabled(!packerWorkflowEnabled);
      showToast(packerWorkflowEnabled ? 'Режим упаковки выключен' : 'Режим упаковки включён');
    } catch (error) {
      // Если есть сообщение об ошибке от сервера, показываем его
      if (error.response && error.response.data && error.response.data.detail) {
        showToast(error.response.data.detail, 'error');
      } else {
        showToast('Ошибка сохранения', 'error');
      }
    }
  };

  const allTimeSlotOptions = (settings.time_slots || [])
    .map(value => ({ value, label: value.replace('-', ' - ') }));

  const handleBlockSlots = async () => {
    if (!blockForm.date_from || !blockForm.date_to) {
      showToast('Укажите даты', 'error');
      return;
    }
    if (blockForm.time_slots.length === 0) {
      showToast('Выберите интервалы', 'error');
      return;
    }
    try {
      await api.createBlockedSlots(blockForm);
      showToast('Интервалы заблокированы');
      setBlockForm({ date_from: '', date_to: '', time_slots: [] });
      loadData();
    } catch (error) {
      showToast('Ошибка: ' + (error.message || 'Неизвестная ошибка'), 'error');
    }
  };

  const handleDeleteBlockedSlot = async (id) => {
    if (!(await nfConfirm('Удалить блокировку?', { confirmText: 'Удалить' }))) return;
    try {
      await api.deleteBlockedSlot(id);
      showToast('Блокировка удалена');
      loadData();
    } catch (error) {
      showToast('Ошибка удаления', 'error');
    }
  };

  const toggleSlotCheckbox = (value) => {
    setBlockForm(prev => {
      const current = prev.time_slots;
      if (value === 'all') {
        return { ...prev, time_slots: current.includes('all') ? [] : ['all'] };
      }
      const withoutAll = current.filter(v => v !== 'all');
      if (withoutAll.includes(value)) {
        return { ...prev, time_slots: withoutAll.filter(v => v !== value) };
      }
      return { ...prev, time_slots: [...withoutAll, value] };
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length === 3) return `${parts[2]}.${parts[1]}.${parts[0]}`;
    return dateStr;
  };

  const saveTariffIntervals = async () => {
    const v = validateTariffRows(tariffRows);
    if (!v.ok) {
      // Не текстом «Строка N…», а переходом: скроллим к битой строке и ставим курсор
      const el = document.querySelector('.tariff-row-error input');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus({ preventScroll: true });
        showToast('Заполните выделенный интервал', 'error');
      } else {
        showToast(v.error, 'error'); // пустой список и т.п. — строки для перехода нет
      }
      return;
    }
    try {
      await api.updateDeliveryTariffIntervals({ intervals: v.intervals });
      showToast('Тарифы доставки обновлены');
    } catch (error) {
      showToast(error.message || 'Ошибка сохранения', 'error');
    }
  };

  const saveWarehouse = async () => {
    if (!Number.isFinite(warehouse.lat) || !Number.isFinite(warehouse.lon)) {
      showToast('Укажите точку склада на карте', 'error');
      return;
    }
    try {
      await api.updateDeliveryTariffIntervals({ warehouse_lat: warehouse.lat, warehouse_lon: warehouse.lon });
      showToast('Координаты склада сохранены');
    } catch (error) {
      showToast(error.message || 'Ошибка сохранения', 'error');
    }
  };

  const openCreatePointModal = () => {
    setEditingPoint(null);
    setPointForm({ name: '', address: '', price: 0, is_active: true });
    setShowModal(true);
  };

  const openEditPointModal = (point) => {
    setEditingPoint(point);
    setPointForm({
      name: point.name,
      address: point.address || '',
      price: point.price || 0,
      is_active: point.is_active !== false
    });
    setShowModal(true);
  };

  const handlePointSubmit = async () => {
    try {
      if (editingPoint) {
        await api.updatePickupPoint(editingPoint.id, pointForm);
        showToast('Точка обновлена');
      } else {
        await api.createPickupPoint(pointForm);
        showToast('Точка создана');
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      showToast('Ошибка сохранения', 'error');
    }
  };

  const deletePoint = async (id) => {
    if (!(await nfConfirm('Удалить точку самовывоза?', { confirmText: 'Удалить' }))) return;

    try {
      await api.deletePickupPoint(id);
      showToast('Точка удалена');
      loadData();
    } catch (error) {
      showToast('Ошибка удаления', 'error');
    }
  };

  // Suppliers methods
  const loadSuppliers = async () => {
    try {
      const data = await api.getSuppliers(showAllSuppliers);
      data.sort((a, b) => a.name.localeCompare(b.name, 'ru'));
      setSuppliers(data);
    } catch {
      showToast('Ошибка загрузки поставщиков', 'error');
    }
  };

  useEffect(() => { loadSuppliers(); }, [showAllSuppliers]);

  const openCreateSupplier = () => {
    setEditingSupplier(null);
    setSupplierForm({ name: '', contact_person: '', phone: '', comment: '', is_active: true });
    setShowSupplierModal(true);
  };

  const openEditSupplier = (supplier) => {
    setEditingSupplier(supplier);
    setSupplierForm({
      name: supplier.name,
      contact_person: supplier.contact_person || '',
      phone: supplier.phone || '',
      comment: supplier.comment || '',
      is_active: supplier.is_active !== false,
    });
    setShowSupplierModal(true);
  };

  const handleSupplierSubmit = async () => {
    if (!supplierForm.name.trim()) {
      showToast('Введите название поставщика', 'error');
      return;
    }
    try {
      if (editingSupplier) {
        await api.updateSupplier(editingSupplier.id, supplierForm);
        showToast('Поставщик обновлён');
      } else {
        await api.createSupplier(supplierForm);
        showToast('Поставщик создан');
      }
      setShowSupplierModal(false);
      loadSuppliers();
    } catch (error) {
      showToast(error.message || 'Ошибка сохранения', 'error');
    }
  };

  const handleDeleteSupplier = async (id) => {
    if (!(await nfConfirm('Деактивировать поставщика? Он будет скрыт из списков выбора.', { confirmText: 'Деактивировать' }))) return;
    try {
      await api.deleteSupplier(id);
      showToast('Поставщик деактивирован');
      loadSuppliers();
    } catch (error) {
      showToast(error.message || 'Ошибка деактивации', 'error');
    }
  };

  if (isLoading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Настройки</h1>
      </div>

      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'general' ? 'active' : ''}`}
          onClick={() => setActiveTab('general')}
        >
          Общие
        </button>
        <button 
          className={`tab ${activeTab === 'restrictions' ? 'active' : ''}`}
          onClick={() => setActiveTab('restrictions')}
        >
          Ограничения
        </button>
        <button 
          className={`tab ${activeTab === 'delivery' ? 'active' : ''}`}
          onClick={() => setActiveTab('delivery')}
        >
          Доставка
        </button>
        <button
          className={`tab ${activeTab === 'pickup' ? 'active' : ''}`}
          onClick={() => setActiveTab('pickup')}
        >
          Точки самовывоза
        </button>
        <button
          className={`tab ${activeTab === 'suppliers' ? 'active' : ''}`}
          onClick={() => setActiveTab('suppliers')}
        >
          Поставщики
        </button>
        <button
          className={`tab ${activeTab === 'transfer_points' ? 'active' : ''}`}
          onClick={() => setActiveTab('transfer_points')}
        >
          Точки перемещения
        </button>
        <button
          className={`tab ${activeTab === 'subscriptions' ? 'active' : ''}`}
          onClick={() => setActiveTab('subscriptions')}
        >
          Подписки
        </button>
        <button
          className={`tab ${activeTab === 'statuses' ? 'active' : ''}`}
          onClick={() => setActiveTab('statuses')}
        >
          Статусы
        </button>
      </div>

      {activeTab === 'statuses' && <OrderStatusesSettings showToast={showToast} />}

      {activeTab === 'transfer_points' && <TransferPointsSettings showToast={showToast} />}

      {activeTab === 'restrictions' && (
        <div className="card">
          <div className="card-title">Блокировка временных интервалов</div>
          <p style={{ color: '#666', fontSize: 14, marginBottom: 20 }}>
            Заблокируйте определённые даты и временные интервалы для заказов
          </p>

          <div className="form-row" style={{ marginBottom: 16 }}>
            <div className="form-group">
              <label className="form-label">Дата с</label>
              <input
                type="date"
                className="form-input"
                value={blockForm.date_from}
                onChange={(e) => {
                  const val = e.target.value;
                  setBlockForm(prev => ({
                    ...prev,
                    date_from: val,
                    date_to: prev.date_to < val ? val : prev.date_to
                  }));
                }}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Дата по</label>
              <input
                type="date"
                className="form-input"
                value={blockForm.date_to}
                min={blockForm.date_from}
                onChange={(e) => setBlockForm(prev => ({ ...prev, date_to: e.target.value }))}
              />
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label className="form-label">Интервалы</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', padding: '6px 12px', background: blockForm.time_slots.includes('all') ? '#FFCEC7' : '#F4F4EF', '--ch': '6px', fontSize: 14 }}>
                <input
                  type="checkbox"
                  checked={blockForm.time_slots.includes('all')}
                  onChange={() => toggleSlotCheckbox('all')}
                />
                Весь день
              </label>
              {allTimeSlotOptions.map(slot => (
                <label key={slot.value} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', padding: '6px 12px', background: blockForm.time_slots.includes(slot.value) ? '#FFCEC7' : '#F4F4EF', '--ch': '6px', fontSize: 14, opacity: blockForm.time_slots.includes('all') ? 0.5 : 1 }}>
                  <input
                    type="checkbox"
                    checked={blockForm.time_slots.includes(slot.value)}
                    disabled={blockForm.time_slots.includes('all')}
                    onChange={() => toggleSlotCheckbox(slot.value)}
                  />
                  {slot.label}
                </label>
              ))}
            </div>
          </div>

          <button className="btn btn-primary" onClick={handleBlockSlots}>
            Заблокировать
          </button>

          {blockedSlots.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <div className="card-title">Активные блокировки</div>
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Даты</th>
                      <th>Интервал</th>
                      <th>Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {blockedSlots.map(slot => (
                      <tr key={slot.id}>
                        <td>
                          {slot.date_from === slot.date_to
                            ? formatDate(slot.date_from)
                            : `${formatDate(slot.date_from)} — ${formatDate(slot.date_to)}`}
                        </td>
                        <td>
                          <span style={{
                            padding: '4px 10px',
                            '--ch': '5px',
                            background: slot.time_slot ? '#F9EBCF' : '#FFCEC7',
                            fontSize: 13,
                            fontWeight: 500
                          }}>
                            {slot.time_slot || 'Весь день'}
                          </span>
                        </td>
                        <td>
                          <button type="button" className="btn-icon is-danger" title="Удалить" aria-label="Удалить"
                            onClick={() => handleDeleteBlockedSlot(slot.id)}
                          >
                            <TrashIcon />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'delivery' && (
        <>
          <div className="card">
            <div className="card-title">Тарифы доставки по расстоянию</div>
            <DeliveryTariffEditor rows={tariffRows} onChange={setTariffRows} />
            <button className="btn btn-primary" style={{ marginTop: 12 }} onClick={saveTariffIntervals}>
              Сохранить тарифы
            </button>
          </div>

          <div className="card">
            <div className="card-title">Склад</div>
            <div style={{ marginBottom: 10 }}>
              <AdminAddressAutocomplete
                value={warehouseAddress}
                placeholder="Найти по адресу: город, улица, дом"
                onChange={(value, data) => {
                  setWarehouseAddress(value);
                  if (data && data.geo_lat && data.geo_lon) {
                    setWarehouse({ lat: parseFloat(data.geo_lat), lon: parseFloat(data.geo_lon) });
                  }
                }}
              />
            </div>
            <WarehouseMapPicker
              lat={warehouse.lat}
              lon={warehouse.lon}
              onChange={async (lat, lon) => {
                setWarehouse({ lat, lon });
                // Перетащили маркер — показываем адрес точки в поисковой строке
                try {
                  const r = await api.geolocateAddress(lat, lon);
                  if (r && r.address) setWarehouseAddress(r.address);
                } catch (e) { /* адрес не определился — не страшно */ }
              }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <span className="tariff-hint">
                {Number.isFinite(warehouse.lat) && Number.isFinite(warehouse.lon)
                  ? `${warehouse.lat}, ${warehouse.lon}`
                  : 'точка не выбрана'}
              </span>
              <details>
                <summary className="tariff-hint" style={{ cursor: 'pointer' }}>ввести координаты вручную</summary>
                <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                  <input
                    type="number" step="0.000001" className="form-input" style={{ width: 170 }}
                    value={Number.isFinite(warehouse.lat) ? warehouse.lat : ''} placeholder="широта"
                    onChange={(e) => setWarehouse(w => ({ ...w, lat: parseFloat(e.target.value) }))}
                  />
                  <input
                    type="number" step="0.000001" className="form-input" style={{ width: 170 }}
                    value={Number.isFinite(warehouse.lon) ? warehouse.lon : ''} placeholder="долгота"
                    onChange={(e) => setWarehouse(w => ({ ...w, lon: parseFloat(e.target.value) }))}
                  />
                </div>
              </details>
              <button className="btn btn-primary btn-sm" style={{ marginLeft: 'auto' }} onClick={saveWarehouse}>
                Сохранить склад
              </button>
            </div>
          </div>
        </>
      )}

      {activeTab === 'general' && (
        <>
          <div className="card">
            <div className="card-title">Сервисный сбор</div>
            <p style={{ color: '#666', fontSize: 14, marginBottom: 16 }}>
              Процент, который добавляется к сумме заказа
            </p>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <NumInput
                className="form-input"
                style={{ width: 100 }}
                value={settings.service_fee_percent}
                onChange={(n) => setSettings(prev => ({ ...prev, service_fee_percent: n }))}
              />
              <span style={{ color: '#666' }}>%</span>
              <button className="btn btn-primary btn-sm" onClick={updateServiceFee}>
                Сохранить
              </button>
            </div>
          </div>

          <AcquiringCard showToast={showToast} />

          <div className="card">
            <div className="card-title">Горизонт планирования каталога</div>
            <p style={{ color: '#666', fontSize: 14, marginBottom: 16 }}>
              На сколько дней вперёд клиенты могут заказывать букеты и стебли.
              Влияет на бейджи «С X мая» в каталоге, доступные даты в календаре и
              расчёт пересечений с плановыми поставками.
            </p>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <NumInput
                integer min={1} max={90} fallback={14}
                className="form-input"
                style={{ width: 100 }}
                value={settings.catalog_horizon_days}
                onChange={(n) => setSettings(prev => ({ ...prev, catalog_horizon_days: n }))}
              />
              <span style={{ color: '#666' }}>дней</span>
              <button className="btn btn-primary btn-sm" onClick={async () => {
                try {
                  await api.updateCatalogHorizon(settings.catalog_horizon_days);
                  showToast('Горизонт каталога обновлён');
                } catch (error) {
                  showToast(error.message || 'Ошибка сохранения', 'error');
                }
              }}>
                Сохранить
              </button>
            </div>
          </div>

          <div className="card">
            <div className="card-title">Порог приёма заказов</div>
            <p style={{ color: '#666', fontSize: 14, marginBottom: 16 }}>
              Положительное значение — за сколько минут до начала интервала закрывается
              приём заказов на витрине. Отрицательное — приём открыт ещё столько минут
              после начала идущего интервала (например, −30 у слота 13:00–15:00
              закрывает приём в 13:30, дальше покупатель видит следующий слот).
              Не влияет на заказы из админки.
            </p>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <NumInput
                integer min={-1440} max={1440}
                className="form-input"
                style={{ width: 100 }}
                value={settings.order_lead_time_minutes}
                onChange={(n) => setSettings(prev => ({ ...prev, order_lead_time_minutes: n }))}
              />
              <span style={{ color: '#666' }}>минут</span>
              <button className="btn btn-primary btn-sm" onClick={async () => {
                try {
                  await api.updateOrderLeadTime(settings.order_lead_time_minutes);
                  showToast('Порог приёма заказов обновлён');
                } catch (error) {
                  showToast(error.message || 'Ошибка сохранения', 'error');
                }
              }}>
                Сохранить
              </button>
            </div>
          </div>

          <div className="card">
            <div className="card-title">Юридические документы</div>
            <p style={{ color: '#666', fontSize: 14, marginBottom: 16 }}>
              Ссылки на оферту и политику ПДн, которые видит покупатель на витрине
              (чекаут, подписка, сертификаты, SMS-авторизация). Меняются без пересборки.
              При публикации новой редакции политики обновите и «версию» — новые согласия
              будут фиксироваться с ней.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label className="form-label">Публичная оферта (URL)</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="https://..."
                  value={settings.oferta_url || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, oferta_url: e.target.value }))}
                />
              </div>
              <div>
                <label className="form-label">Политика ПДн (URL)</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="https://..."
                  value={settings.privacy_policy_url || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, privacy_policy_url: e.target.value }))}
                />
              </div>
              <div>
                <label className="form-label">Версия политики (дата редакции, YYYY-MM-DD)</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="2026-04-22"
                  value={settings.privacy_policy_version || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, privacy_policy_version: e.target.value }))}
                />
              </div>
              <div>
                <button className="btn btn-primary btn-sm" onClick={async () => {
                  try {
                    await api.updateLegalLinks({
                      oferta_url: settings.oferta_url || '',
                      privacy_policy_url: settings.privacy_policy_url || '',
                      privacy_policy_version: settings.privacy_policy_version || '',
                    });
                    showToast('Юридические ссылки обновлены');
                  } catch (error) {
                    showToast(error.message || 'Ошибка сохранения', 'error');
                  }
                }}>
                  Сохранить
                </button>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-title">Отзывы</div>
            <p style={{ color: '#666', fontSize: 14, marginBottom: 16 }}>
              Ссылки на отзывы показываются кнопками в сообщении о завершении заказа
              (Telegram и Max). Пустое поле — кнопка не показывается.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label className="form-label">Ссылка на 2GIS</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="https://2gis.ru/..."
                  value={settings.review_2gis_url || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, review_2gis_url: e.target.value }))}
                />
              </div>
              <div>
                <label className="form-label">Ссылка на Яндекс.Карты</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="https://yandex.ru/maps/..."
                  value={settings.review_yandex_url || ''}
                  onChange={(e) => setSettings(prev => ({ ...prev, review_yandex_url: e.target.value }))}
                />
              </div>
              <div>
                <button className="btn btn-primary btn-sm" onClick={async () => {
                  try {
                    await api.updateReviewLinks({
                      review_2gis_url: settings.review_2gis_url || '',
                      review_yandex_url: settings.review_yandex_url || '',
                    });
                    showToast('Ссылки на отзывы обновлены');
                  } catch (error) {
                    showToast(error.message || 'Ошибка сохранения', 'error');
                  }
                }}>
                  Сохранить
                </button>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-title">Сервисный сбор за букет (заказы с улицы)</div>
            <p style={{ color: '#666', fontSize: 14, marginBottom: 16 }}>
              Для охапки используется стандартный сервисный сбор выше. Для букета — отдельный процент.
            </p>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <NumInput
                className="form-input"
                style={{ width: 100 }}
                value={settings.bouquet_service_fee_percent}
                onChange={(n) => setSettings(prev => ({ ...prev, bouquet_service_fee_percent: n }))}
              />
              <span style={{ color: '#666' }}>%</span>
              <button className="btn btn-primary btn-sm" onClick={async () => {
                try {
                  await api.updateWalkinServiceFee(settings.bouquet_service_fee_percent);
                  showToast('Сервисный сбор за букет обновлён');
                } catch (error) {
                  showToast('Ошибка сохранения', 'error');
                }
              }}>
                Сохранить
              </button>
            </div>
          </div>

          <div className="card">
            <div className="card-title">Пополнение счёта {BRAND_NAME} за заказ</div>
            <p style={{ color: '#666', fontSize: 14, marginBottom: 16 }}>
              Процент от суммы заказа, который начисляется клиенту на счёт {BRAND_NAME} (целое число)
            </p>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <NumInput
                className="form-input"
                style={{ width: 100 }}
                value={settings.bonus_percent}
                onChange={(n) => setSettings(prev => ({ ...prev, bonus_percent: n }))}
              />
              <span style={{ color: '#666' }}>% от суммы</span>
              <button className="btn btn-primary btn-sm" onClick={updateBonusPercent}>
                Сохранить
              </button>
            </div>
          </div>

          <div className="card">
            <div className="card-title">Подарочные сертификаты</div>
            <p style={{ color: '#666', fontSize: 14, marginBottom: 16 }}>
              Параметры покупки и активации сертификатов
            </p>
            <div style={{ display: 'grid', gap: 12, maxWidth: 480 }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ flex: '1 1 160px', color: '#444' }}>Минимальная сумма</span>
                <NumInput
                  integer min={0}
                  className="form-input"
                  style={{ width: 120 }}
                  value={certConfig.min_amount}
                  onChange={(n) => setCertConfig(prev => ({ ...prev, min_amount: n }))}
                />
                <span style={{ color: '#666' }}>₽</span>
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ flex: '1 1 160px', color: '#444' }}>Максимальная сумма</span>
                <NumInput
                  integer min={0}
                  className="form-input"
                  style={{ width: 120 }}
                  value={certConfig.max_amount}
                  onChange={(n) => setCertConfig(prev => ({ ...prev, max_amount: n }))}
                />
                <span style={{ color: '#666' }}>₽</span>
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ flex: '1 1 160px', color: '#444' }}>Срок активации</span>
                <NumInput
                  integer min={0}
                  className="form-input"
                  style={{ width: 120 }}
                  value={certConfig.validity_days}
                  onChange={(n) => setCertConfig(prev => ({ ...prev, validity_days: n }))}
                />
                <span style={{ color: '#666' }}>дней с момента оплаты</span>
              </div>
              <label style={{ display: 'flex', gap: 10, alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!certConfig.show_banner}
                  onChange={(e) => setCertConfig(prev => ({ ...prev, show_banner: e.target.checked }))}
                />
                <span>Показывать CTA-баннер «Подари букет» на главной</span>
              </label>
              <div>
                <button className="btn btn-primary btn-sm" onClick={updateCertConfig}>
                  Сохранить
                </button>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-title">Минимальная сумма заказа стеблей</div>
            <p style={{ color: '#666', fontSize: 14, marginBottom: 16 }}>
              Минимальная сумма заказа стеблей (не включает открытку и доставку)
            </p>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <NumInput
                className="form-input"
                style={{ width: 120 }}
                value={settings.min_order_amount}
                onChange={(n) => setSettings(prev => ({ ...prev, min_order_amount: n }))}
              />
              <span style={{ color: '#666' }}>₽</span>
              <button className="btn btn-primary btn-sm" onClick={updateMinOrderAmount}>
                Сохранить
              </button>
            </div>
          </div>

          <div className="card">
            <div className="card-title">Стоимость открытки</div>
            <p style={{ color: '#666', fontSize: 14, marginBottom: 16 }}>
              Стоимость открытки с пожеланием (добавляется к заказу)
            </p>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <NumInput
                className="form-input"
                style={{ width: 120 }}
                value={settings.postcard_price}
                onChange={(n) => setSettings(prev => ({ ...prev, postcard_price: n }))}
              />
              <span style={{ color: '#666' }}>₽</span>
              <button className="btn btn-primary btn-sm" onClick={updatePostcardPrice}>
                Сохранить
              </button>
            </div>
          </div>

          <div className="card">
            <div className="card-title">Комиссия с доставки</div>
            <p style={{ color: '#666', fontSize: 14, marginBottom: 16 }}>
              Процент, который мы забираем из стоимости доставки. Курьер получает остаток.
            </p>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <NumInput
                min={0} max={100}
                className="form-input"
                style={{ width: 120 }}
                value={courierCommission}
                onChange={setCourierCommission}
              />
              <span style={{ color: '#666' }}>%</span>
              <button className="btn btn-primary btn-sm" onClick={updateCourierCommission}>
                Сохранить
              </button>
            </div>
            <small style={{ display: 'block', marginTop: 8, color: '#999', fontSize: 13 }}>
              Например, при комиссии 20% из доставки 500₽ курьер получит 400₽
            </small>
          </div>

          <div className="card">
            <div className="card-title">Режим упаковки</div>
            <p style={{ color: '#666', fontSize: 14, marginBottom: 16 }}>
              Включайте на горячие даты (14 февраля, 8 марта). Когда включен, флористы передают букеты упаковщикам. Когда выключен, флористы упаковывают сами.
            </p>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={packerWorkflowEnabled}
                  onChange={togglePackerWorkflow}
                  style={{ width: 20, height: 20, cursor: 'pointer' }}
                />
                <span style={{ fontSize: 15, fontWeight: 500 }}>
                  {packerWorkflowEnabled ? 'Включён' : 'Выключен'}
                </span>
              </label>
            </div>
          </div>

          <div className="card">
            <div className="card-title">Фото-контроль</div>
            <p style={{ color: '#666', fontSize: 14, marginBottom: 16 }}>
              Флорист, назначенный на заказ, или админ добавляет фото собранного заказа; без фото заказ не передать
              дальше: «на упаковку» в режиме упаковки или «Собран» без него. Упаковщик и курьер фото не делают.
              Лимит — 2 фото на позицию (букет, сборный букет или позиция стеблей). Клиентам фото не отправляются.
              У заказов Flowwow фото обязательно всегда — его требует площадка, оно уходит покупателю.
            </p>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={photoControlEnabled} onChange={togglePhotoControl} style={{ width: 20, height: 20, cursor: 'pointer' }} />
                <span style={{ fontSize: 15, fontWeight: 500 }}>{photoControlEnabled ? 'Включён' : 'Выключен'}</span>
              </label>
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 14, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 14 }}>Хранить фото, дней:</span>
              <input type="number" min="1" max="365" value={photoRetentionDays} onChange={e => setPhotoRetentionDays(e.target.value)}
                className="form-input" style={{ width: 90 }} />
              <button className="btn btn-secondary btn-sm" onClick={savePhotoRetention}>Сохранить</button>
              <small style={{ color: '#999', fontSize: 12 }}>Старше срока удаляются с сервера ночью; отметка «фото отправлено» у заказа остаётся.</small>
            </div>
          </div>

          <div className="card">
            <div className="card-title">Лимит количества в заказе</div>
            <p style={{ color: '#666', fontSize: 14, marginBottom: 16 }}>
              Максимальное количество одной позиции, которое клиент может добавить в один заказ.
              На витрине остаток тоже не будет показываться больше этого числа. Оставьте пустым — без ограничений.
            </p>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', marginBottom: 8 }}>
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <label className="form-label">Макс. кол-во (пусто = без лимита)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="form-input"
                  value={settings.max_display_quantity || ''}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    setSettings(prev => ({ ...prev, max_display_quantity: value ? parseInt(value) : null }));
                  }}
                  placeholder="Например: 100"
                />
              </div>
              <button className="btn btn-primary btn-sm" onClick={updateMaxDisplayQuantity}>
                Сохранить
              </button>
            </div>
          </div>
        </>
      )}

      {activeTab === 'pickup' && (
        <>
          <div style={{ marginBottom: 16 }}>
            <button className="btn btn-primary" onClick={openCreatePointModal}>
              + Добавить точку
            </button>
          </div>

          {pickupPoints.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">📍</div>
              <div className="empty-state-text">Точек самовывоза пока нет</div>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Название</th>
                    <th>Адрес</th>
                    <th>Цена</th>
                    <th>Статус</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {pickupPoints.map(point => (
                    <tr key={point.id}>
                      <td><strong>{point.name}</strong></td>
                      <td>{point.address || '—'}</td>
                      <td>{point.price > 0 ? `${point.price} ₽` : 'Бесплатно'}</td>
                      <td>
                        <span className={`badge badge-${point.is_active ? 'active' : 'inactive'}`}>
                          {point.is_active ? 'Активна' : 'Скрыта'}
                        </span>
                      </td>
                      <td>
                        <div className="table-actions">
                          <button type="button" className="btn-icon" title="Изменить" aria-label="Изменить"
                            onClick={() => openEditPointModal(point)}
                          >
                            <EditIcon />
                          </button>
                          <button type="button" className="btn-icon is-danger" title="Удалить" aria-label="Удалить"
                            onClick={() => deletePoint(point.id)}
                          >
                            <TrashIcon />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {activeTab === 'suppliers' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <label style={{ fontSize: 13, color: '#666', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                type="checkbox"
                checked={showAllSuppliers}
                onChange={(e) => setShowAllSuppliers(e.target.checked)}
              />
              Показать неактивных
            </label>
            <button className="btn btn-primary" onClick={openCreateSupplier}>
              + Добавить
            </button>
          </div>

          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Название</th>
                  <th>Контактное лицо</th>
                  <th>Телефон</th>
                  <th>Статус</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {suppliers.length === 0 ? (
                  <tr>
                    <td colSpan="5" style={{ textAlign: 'center', color: '#999', padding: '32px 16px' }}>
                      Поставщиков пока нет
                    </td>
                  </tr>
                ) : suppliers.map(supplier => (
                  <tr key={supplier.id} style={{ opacity: supplier.is_active === false ? 0.5 : 1 }}>
                    <td style={{ padding: '8px 12px', fontWeight: 500 }}>{supplier.name}</td>
                    <td style={{ padding: '8px 12px', color: supplier.contact_person ? '#333' : '#999' }}>
                      {supplier.contact_person || '—'}
                    </td>
                    <td style={{ padding: '8px 12px', color: supplier.phone ? '#333' : '#999' }}>
                      {supplier.phone || '—'}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <span className={`badge badge-${supplier.is_active !== false ? 'active' : 'inactive'}`}>
                        {supplier.is_active !== false ? 'Активен' : 'Неактивен'}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <div className="table-actions">
                        <button type="button" className="btn-icon" title="Изменить" aria-label="Изменить" onClick={() => openEditSupplier(supplier)}>
                          <EditIcon />
                        </button>
                        {supplier.is_active !== false && (
                          <button type="button" className="btn-icon is-danger" title="Удалить" aria-label="Удалить" onClick={() => handleDeleteSupplier(supplier.id)}>
                            <TrashIcon />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {showSupplierModal && (
        <Modal
          title={editingSupplier ? 'Редактировать поставщика' : 'Новый поставщик'}
          onClose={() => setShowSupplierModal(false)}
          confirmOverlayClose
          footer={
            <>
              <button className="btn btn-secondary" onClick={() => setShowSupplierModal(false)}>Отмена</button>
              <button className="btn btn-primary" onClick={handleSupplierSubmit}>
                {editingSupplier ? 'Сохранить' : 'Создать'}
              </button>
            </>
          }
        >
          <div className="form-group">
            <label className="form-label">Название</label>
            <input type="text" className="form-input" placeholder="ООО Цветочная база"
              value={supplierForm.name} autoFocus
              onChange={(e) => setSupplierForm(prev => ({ ...prev, name: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Контактное лицо</label>
            <input type="text" className="form-input" placeholder="Иван Иванов"
              value={supplierForm.contact_person}
              onChange={(e) => setSupplierForm(prev => ({ ...prev, contact_person: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Телефон</label>
            <input type="text" className="form-input" placeholder="+7 999 123-45-67"
              value={supplierForm.phone}
              onChange={(e) => setSupplierForm(prev => ({ ...prev, phone: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Комментарий</label>
            <textarea className="form-input" placeholder="Заметки о поставщике" rows={3}
              value={supplierForm.comment} style={{ resize: 'vertical' }}
              onChange={(e) => setSupplierForm(prev => ({ ...prev, comment: e.target.value }))} />
          </div>
          {editingSupplier && (
            <div className="form-group">
              <label className="form-checkbox">
                <input type="checkbox" checked={supplierForm.is_active}
                  onChange={(e) => setSupplierForm(prev => ({ ...prev, is_active: e.target.checked }))} />
                Активен (доступен для выбора в накладных)
              </label>
            </div>
          )}
        </Modal>
      )}

      {activeTab === 'subscriptions' && (
        <>
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-title">Доставка подписок</div>
          <p style={{ color: '#5E6561', fontSize: 14, marginBottom: 16 }}>
            Применяется к новым подпискам — старые не пересчитываются. Курьерская выплата считается из стандартного тарифа в любом случае.
          </p>
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 12 }}>
            <div style={{ minWidth: 280 }}>
              <label style={{ display: 'block', fontSize: 13, color: '#5E6561', marginBottom: 4 }}>Что платит клиент за доставку</label>
              <select
                value={settings.subscription_delivery_mode || 'free'}
                onChange={e => setSettings(prev => ({ ...prev, subscription_delivery_mode: e.target.value }))}
                style={{ width: '100%', padding: '9px 12px', fontSize: 14, border: '1px solid #D8DDDA', '--ch': '5px', '--stroke': '#D8DDDA', color: '#1C3D2E', background: '#fff' }}
              >
                <option value="free">Бесплатно (0 ₽)</option>
                <option value="standard">Стандартные тарифы (по адресу/зоне)</option>
              </select>
            </div>
            <button
              className="btn btn-primary"
              style={{ height: 40 }}
              onClick={async () => {
                try {
                  await api.updateSubscriptionDelivery(settings.subscription_delivery_mode || 'free');
                  showToast('Режим доставки подписок обновлён');
                } catch (e) {
                  showToast('Ошибка сохранения', 'error');
                }
              }}
            >
              Сохранить
            </button>
          </div>
        </div>
        <div className="card">
          <div className="card-title">Скидки за длительность подписки</div>
          <p style={{ color: '#666', fontSize: 14, marginBottom: 16 }}>
            Настройте пороги скидок в зависимости от количества доставок
          </p>

          <table className="table" style={{ marginBottom: 16 }}>
            <thead>
              <tr>
                <th>Мин. доставок</th>
                <th>Скидка %</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {discountTiers.map((tier) => (
                <tr key={tier.id || tier.min_deliveries}>
                  <td>{tier.min_deliveries}</td>
                  <td>{tier.discount_percent}%</td>
                  <td>
                    <button
                      className="btn btn-danger"
                      style={{ padding: '4px 10px', fontSize: 13 }}
                      onClick={async () => {
                        const updated = discountTiers.filter(t => t.min_deliveries !== tier.min_deliveries);
                        try {
                          await api.updateSubscriptionDiscountTiers(updated.map(t => ({ min_deliveries: t.min_deliveries, discount_percent: t.discount_percent })));
                          setDiscountTiers(updated);
                          showToast('Порог удалён');
                        } catch (e) {
                          showToast('Ошибка удаления', 'error');
                        }
                      }}
                    >
                      Удалить
                    </button>
                  </td>
                </tr>
              ))}
              {discountTiers.length === 0 && (
                <tr><td colSpan="3" style={{ textAlign: 'center', color: '#999' }}>Нет порогов скидок</td></tr>
              )}
            </tbody>
          </table>

          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <div>
              <label style={{ fontSize: 13, color: '#666' }}>Мин. доставок</label>
              <input
                type="number"
                min="2"
                value={newTier.min_deliveries}
                onChange={(e) => setNewTier(prev => ({ ...prev, min_deliveries: e.target.value }))}
                style={{ display: 'block', padding: '6px 10px', border: '2px solid #ddd', '--ch': '5px', '--stroke': '#ddd', '--bw': '2px', width: 100 }}
              />
            </div>
            <div>
              <label style={{ fontSize: 13, color: '#666' }}>Скидка %</label>
              <input
                type="number"
                min="1"
                max="100"
                value={newTier.discount_percent}
                onChange={(e) => setNewTier(prev => ({ ...prev, discount_percent: e.target.value }))}
                style={{ display: 'block', padding: '6px 10px', border: '2px solid #ddd', '--ch': '5px', '--stroke': '#ddd', '--bw': '2px', width: 100 }}
              />
            </div>
            <button
              className="btn btn-primary"
              style={{ padding: '6px 16px' }}
              onClick={async () => {
                const minDel = parseInt(newTier.min_deliveries);
                const discPct = parseInt(newTier.discount_percent);
                if (!minDel || minDel < 2 || !discPct || discPct < 1 || discPct > 100) {
                  showToast('Заполните корректные значения', 'error');
                  return;
                }
                if (discountTiers.some(t => t.min_deliveries === minDel)) {
                  showToast('Порог уже существует', 'error');
                  return;
                }
                const updated = [...discountTiers, { min_deliveries: minDel, discount_percent: discPct }]
                  .sort((a, b) => a.min_deliveries - b.min_deliveries);
                try {
                  await api.updateSubscriptionDiscountTiers(updated.map(t => ({ min_deliveries: t.min_deliveries, discount_percent: t.discount_percent })));
                  setDiscountTiers(updated);
                  setNewTier({ min_deliveries: '', discount_percent: '' });
                  showToast('Порог добавлен');
                } catch (e) {
                  showToast('Ошибка сохранения', 'error');
                }
              }}
            >
              Добавить
            </button>
          </div>

          <div style={{ marginTop: 32, borderTop: '1px solid #eee', paddingTop: 20 }}>
            <div className="card-title" style={{ fontSize: 16 }}>Замена вазы</div>
            <p style={{ color: '#666', fontSize: 14, marginBottom: 12 }}>
              Бесплатная замена вазы при каждой доставке (доступна при достаточном количестве доставок)
            </p>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <div>
                <label style={{ fontSize: 13, color: '#666' }}>Мин. кол-во доставок</label>
                <NumInput
                  integer min={1} fallback={4}
                  value={settings.vase_min_deliveries || 4}
                  onChange={(n) => setSettings(prev => ({ ...prev, vase_min_deliveries: n }))}
                  style={{ display: 'block', padding: '6px 10px', border: '2px solid #ddd', '--ch': '5px', '--stroke': '#ddd', '--bw': '2px', width: 100 }}
                />
              </div>
              <button
                className="btn btn-primary"
                style={{ padding: '6px 16px' }}
                onClick={async () => {
                  try {
                    await api.updateVaseMinDeliveries(settings.vase_min_deliveries || 4);
                    showToast('Настройка сохранена');
                  } catch (e) {
                    showToast('Ошибка сохранения', 'error');
                  }
                }}
              >
                Сохранить
              </button>
            </div>
          </div>
        </div>
        </>
      )}

      {showModal && (
        <Modal
          title={editingPoint ? 'Редактировать точку' : 'Новая точка самовывоза'}
          onClose={() => setShowModal(false)}
          confirmOverlayClose
          footer={
            <>
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>
                Отмена
              </button>
              <button className="btn btn-primary" onClick={handlePointSubmit}>
                {editingPoint ? 'Сохранить' : 'Создать'}
              </button>
            </>
          }
        >
          <div className="form-group">
            <label className="form-label">Название</label>
            <input
              type="text"
              className="form-input"
              placeholder="Например: Магазин на Баумана"
              value={pointForm.name}
              onChange={(e) => setPointForm(prev => ({ ...prev, name: e.target.value }))}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Адрес</label>
            <input
              type="text"
              className="form-input"
              placeholder="г. Казань, ул. Баумана, д. 42"
              value={pointForm.address}
              onChange={(e) => setPointForm(prev => ({ ...prev, address: e.target.value }))}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Стоимость доставки (₽)</label>
            <NumInput
              min={0}
              className="form-input"
              placeholder="0"
              value={pointForm.price}
              onChange={(n) => setPointForm(prev => ({ ...prev, price: n }))}
            />
            <small style={{ color: '#666', fontSize: 12 }}>0 = бесплатно</small>
          </div>

          <div className="form-group">
            <label className="form-checkbox">
              <input
                type="checkbox"
                checked={pointForm.is_active}
                onChange={(e) => setPointForm(prev => ({ ...prev, is_active: e.target.checked }))}
              />
              Активна (доступна для выбора)
            </label>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default SettingsPage;
